import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Icon_Checkmark24,
  isTouchDevice,
  Menu,
  TagInput,
  type TagInputRef,
} from "@shapr3d/frontend-lib";
import { difference } from "es-toolkit";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import usePrevious from "../../../../../hooks/usePrevious";
import type {
  ProjectInvitationResponse,
  ProjectSharingBaseRole,
} from "../../../../../hooks/useProjectSharing";
import {
  type WebExportBaseRole,
  WebExportUserRolesDisplayNames,
} from "../../../../../hooks/useWebExport";
import { isEmailInvalid } from "../../../../../utils/helpers";
import { Icon_FillerSvg24 } from "./FillerSvg";
import { SelectActivator } from "./SelectActivator";
import { SingleIconLabel } from "./SingleIconLable";

import style from "./EmailInvitationForm.module.css";

const baseRoleOptions: { [key in WebExportBaseRole | ProjectSharingBaseRole]: string } = {
  viewer: "Viewer",
  commenter: "Commenter",
} as const;

type AllBaseRoles = keyof typeof baseRoleOptions;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getValidBaseRolesFromSchemaObject(schema: z.ZodObject<any>): AllBaseRoles[] {
  const baseRoleField = schema.shape.baseRole;
  if (baseRoleField instanceof z.ZodEnum) {
    return baseRoleField.options as AllBaseRoles[];
  }
  if (baseRoleField instanceof z.ZodUnion) {
    const literals = baseRoleField._def.options
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((opt: any): opt is z.ZodLiteral<AllBaseRoles> => opt instanceof z.ZodLiteral)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((lit: any) => lit.value);
    return literals;
  }
  return [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getRoleOptionsForSchema(schema: z.ZodObject<any>): Record<AllBaseRoles, string> {
  const validRoles = getValidBaseRolesFromSchemaObject(schema);
  return Object.fromEntries(validRoles.map((role) => [role, baseRoleOptions[role]])) as Record<
    AllBaseRoles,
    string
  >;
}

interface EmailInvitationProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: z.ZodObject<any>;
  isPending?: boolean;
  defaultRole: WebExportBaseRole | ProjectSharingBaseRole;
  invalidEmailList: string[];
  isError?: boolean;
  onSubmit: (data: {
    emails: string[];
    baseRole: WebExportBaseRole | ProjectSharingBaseRole;
  }) => Promise<ProjectInvitationResponse>;

  onEmailAdded: () => void;
  onInvalidEmailAdded: () => void;
  onEmailRemoved: () => void;
  onRoleChanged: (
    newRole: WebExportBaseRole | ProjectSharingBaseRole,
    oldRole: WebExportBaseRole | ProjectSharingBaseRole
  ) => void;
  onInvitedPressed: (role: WebExportBaseRole | ProjectSharingBaseRole) => void;
}

export const EmailInvitationForm = ({
  schema,
  isPending,
  defaultRole,
  invalidEmailList,
  isError,
  onSubmit,
  onEmailAdded,
  onInvalidEmailAdded,
  onEmailRemoved,
  onRoleChanged,
  onInvitedPressed,
}: EmailInvitationProps) => {
  const roleOptions = getRoleOptionsForSchema(schema);
  const [currentInputValue, setCurrentInputValue] = useState("");
  const [isCurrentInputValid, setIsCurrentInputValid] = useState(false);
  const tagInputRef = useRef<TagInputRef | null>(null);

  const {
    handleSubmit,
    formState: { isSubmitted, isSubmitting },
    control,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      emails: [],
      baseRole: defaultRole,
    },
    resolver: zodResolver(schema),
  });

  const emailsWatch = useWatch({ name: "emails", control });
  const baseRoleWatch = useWatch({ name: "baseRole", control });

  const { validEmails, invalidEmails } = useMemo((): {
    validEmails: string[];
    invalidEmails: string[];
  } => {
    const validationErrorEmails = emailsWatch.filter(isEmailInvalid);
    const invalidEmails = [...validationErrorEmails, ...invalidEmailList];

    return {
      validEmails: emailsWatch.filter((email) => !invalidEmails.includes(email)),
      invalidEmails,
    };
  }, [emailsWatch, invalidEmailList]);

  const prevValidEmails = usePrevious(validEmails);
  const prevInvalidEmails = usePrevious(invalidEmails);
  const prevEmails = usePrevious(emailsWatch);

  useEffect(() => {
    if (isSubmitting || isSubmitted) {
      return;
    }

    if (prevValidEmails && prevValidEmails.length < validEmails.length) {
      const diff = difference(validEmails, prevValidEmails);
      diff.map(onEmailAdded);
      return;
    }
    if (prevInvalidEmails && prevInvalidEmails.length < invalidEmails.length) {
      const diff = difference(invalidEmails, prevInvalidEmails);
      diff.map(onInvalidEmailAdded);
      return;
    }
    if (
      prevEmails &&
      prevEmails.length < emailsWatch.length &&
      emailsWatch.some((email) => invalidEmails.includes(email))
    ) {
      const diff = difference(emailsWatch, prevEmails);
      diff.map(onInvalidEmailAdded);
      return;
    }
  }, [
    validEmails,
    prevValidEmails,
    prevInvalidEmails,
    invalidEmails,
    isSubmitting,
    isSubmitted,
    emailsWatch,
    prevEmails,
    onEmailAdded,
    onInvalidEmailAdded,
  ]);

  const submit = useCallback(async () => {
    const emailsSet = new Set([...validEmails]);

    if (isCurrentInputValid && currentInputValue.trim() !== "") {
      const trimmedInput = currentInputValue.trim();
      emailsSet.add(trimmedInput);
    }

    const emailsToSubmit = Array.from(emailsSet);

    await onSubmit({ emails: emailsToSubmit, baseRole: baseRoleWatch });

    reset();
    setCurrentInputValue("");
    setIsCurrentInputValid(false);
    tagInputRef.current?.clearInput();
  }, [onSubmit, validEmails, baseRoleWatch, reset, isCurrentInputValid, currentInputValue]);

  const handleInputChange = useCallback((value: string) => {
    setCurrentInputValue(value);
    setIsCurrentInputValid(!isEmailInvalid(value.trim()));
  }, []);

  const isSubmitDisabled = useMemo(() => {
    const hasInvalidInput = currentInputValue.trim() !== "" && !isCurrentInputValid;

    const hasValidEmails = validEmails.length > 0;
    const hasValidInput = isCurrentInputValid && currentInputValue.trim() !== "";

    return isPending || hasInvalidInput || (!hasValidEmails && !hasValidInput) || isSubmitting;
  }, [currentInputValue, isCurrentInputValid, validEmails.length, isPending, isSubmitting]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className={style.formContainer}>
        <Controller
          control={control}
          name="emails"
          render={({ field: { name, onBlur, onChange, value } }) => (
            <TagInput
              ref={tagInputRef}
              className={style.tagInput}
              values={value}
              disabled={isPending}
              name={name}
              placeholder="Emails"
              maxRows={4}
              autoFocus={!isTouchDevice()}
              onBlur={(_emails) => {
                onBlur();
              }}
              onChange={onChange}
              errors={invalidEmails}
              onRemove={onEmailRemoved}
              onAdd={() => {
                setCurrentInputValue("");
                setIsCurrentInputValid(false);
              }}
              onInput={(e) => handleInputChange(e.currentTarget.value)}
              onKeyDown={(e) => {
                const target = e.currentTarget as HTMLInputElement;

                if (e.key === "Enter" && validEmails.length > 0 && target.value === "") {
                  e.preventDefault();
                  handleSubmit(submit)();
                  return;
                }
              }}
            />
          )}
        />
        <div className={style.formButtonsContainer}>
          <Menu
            activator={
              <SelectActivator
                aria-label="Role"
                disabled={isPending}
                label={WebExportUserRolesDisplayNames[baseRoleWatch]}
                variant="grayscale"
              />
            }
          >
            {Object.entries(roleOptions).map(([baseRole, label]) => (
              <Menu.Item
                key={baseRole}
                closeOnClick
                onClick={(event) => {
                  event.preventDefault();

                  if (baseRole === baseRoleWatch) {
                    return;
                  }

                  setValue("baseRole", baseRole);
                  onRoleChanged(
                    baseRole as WebExportBaseRole | ProjectSharingBaseRole,
                    baseRoleWatch
                  );
                }}
              >
                <SingleIconLabel
                  icon={baseRole === baseRoleWatch ? <Icon_Checkmark24 /> : <Icon_FillerSvg24 />}
                  label={label}
                />
              </Menu.Item>
            ))}
          </Menu>

          <Button
            disabled={isSubmitDisabled}
            type="submit"
            onClick={() => {
              onInvitedPressed(baseRoleWatch);
            }}
          >
            Invite
          </Button>
        </div>
      </div>
      {isError && <span className={style.red}>{"Something went wrong. Please try again."}</span>}
    </form>
  );
};
